import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { useDeferredPaint } from '@blockworks/platform/hooks';

import { safeAttr } from '../../utils';
import { Button } from '../button';

import { expandableContentStyles } from './expandable-content.styles';

type ExpandableContentProps = PropsWithChildren<{
    maxHeight: number;
}>;

const ExpandableContent = ({ children, maxHeight }: ExpandableContentProps) => {
    const ready = useDeferredPaint();
    const contentRef = useRef<HTMLDivElement>(null);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);

    const showOverlay = isTruncated && !isExpanded;
    const contentHeight = contentRef?.current?.clientHeight ?? 0;

    useEffect(() => {
        const shouldTruncate = contentHeight - 16 > maxHeight;

        if (shouldTruncate) {
            setIsTruncated(shouldTruncate);
        }
    }, [ready, contentHeight, maxHeight]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const { base, buttonContainer, contentContainer, overlay } = expandableContentStyles();
    const isExpandedDataAttr = safeAttr(isExpanded, 'open', { fallbackValue: 'closed' });

    return (
        <div
            className={base({})}
            {...safeAttr(isTruncated, 'truncated')}
            style={
                {
                    '--open-height': `${contentHeight}px`,
                    '--closed-height': `${maxHeight}px`,
                    '--expand-duration': `${Math.min(Math.round(contentHeight / 200) * 100, 1000)}ms`,
                } as {}
            }
        >
            <div className={contentContainer()} {...isExpandedDataAttr}>
                {showOverlay && <div className={overlay({})} />}
                <div ref={contentRef}>{children}</div>
            </div>
            {isTruncated && (
                <div
                    className={buttonContainer()}
                    {...isExpandedDataAttr}
                    {...safeAttr(isExpanded, 'open', { fallbackValue: 'closed' })}
                >
                    <Button onClick={toggleExpand}>{isExpanded ? 'Show less' : 'Show more'}</Button>
                </div>
            )}
        </div>
    );
};

export type { ExpandableContentProps };
export { ExpandableContent };
